/**
 * ASCAUTO — Iter 184 — Modal reutilizable de cambio de contraseña.
 * Usado desde el menu de usuario (esquina superior derecha) y desde UsersPage.
 * Aplica politica via /api/auth/change-password (backend valida 12/14 caracteres,
 * historial, datos personales, etc.). Marca must_change_password=false al ok.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { evaluatePassword } from "@/lib/passwordPolicy";
import { Lock, KeyRound, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange, onSuccess }) {
  const { axiosAuth, user } = useAuth();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const role = user?.role || "";
  const isAdminRole = role === "admin" || role === "super_admin";
  const minLen = isAdminRole ? 14 : 12;

  const evaluation = form.next ? evaluatePassword(form.next, {
    userData: { name: user?.name, email: user?.email },
    role,
  }) : null;

  const passwordsMatch = form.next && form.next === form.confirm;
  const sameAsCurrent = form.current && form.next && form.current === form.next;
  const canSubmit = form.current && form.next && passwordsMatch && evaluation?.is_valid && !sameAsCurrent;

  const reset = () => setForm({ current: "", next: "", confirm: "" });

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axiosAuth().post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Contraseña actualizada correctamente");
      reset();
      onOpenChange?.(false);
      onSuccess?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (typeof msg === "string") toast.error(msg);
      else if (msg?.reasons) toast.error(msg.reasons[0] || "Contraseña insegura");
      else toast.error("Error al cambiar la contraseña");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange?.(o); }}>
      <DialogContent className="max-w-md rounded-sm" data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="text-base uppercase flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> Cambiar contraseña
          </DialogTitle>
          <DialogDescription className="text-xs">
            Mínimo {minLen} caracteres {isAdminRole && "(requisito reforzado para rol administrativo)"}.
            Debe ser distinta a la actual y no contener tus datos personales.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Contraseña actual *</label>
            <Input type="password" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })}
                   className="rounded-sm text-sm" autoComplete="current-password" data-testid="cp-current" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Nueva contraseña *</label>
            <Input type="password" value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })}
                   className="rounded-sm text-sm" autoComplete="new-password" data-testid="cp-new" />
            {evaluation && (
              <div className={`mt-1 text-[11px] flex items-center gap-1.5 ${evaluation.is_valid ? "text-emerald-700" : "text-red-700"}`} data-testid="cp-policy-status">
                {evaluation.is_valid ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {evaluation.is_valid
                  ? `Cumple la política (fuerza: ${evaluation.strength_label || "OK"})`
                  : evaluation.reasons[0]}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Repetir nueva contraseña *</label>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                   className="rounded-sm text-sm" autoComplete="new-password" data-testid="cp-confirm" />
            {form.confirm && !passwordsMatch && (
              <p className="text-[11px] text-red-700 mt-1">Las contraseñas no coinciden</p>
            )}
            {sameAsCurrent && (
              <p className="text-[11px] text-red-700 mt-1">La nueva no puede ser igual a la actual</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-sm text-xs" disabled={busy}>Cancelar</Button>
          <Button size="sm" disabled={!canSubmit || busy} onClick={handleSubmit} className="rounded-sm text-xs" data-testid="cp-submit">
            <Lock className="w-3 h-3 mr-1" /> Guardar nueva contraseña
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
